import { imageRecipeForCard } from '../utility/ImageRecipeUtil';
import { CardSize, CardType, CmsProperty } from '../../api/models/Shared.types';
import { CmsConstants } from './CmsConstants';
import { ICustomCmsRibbon, IRibbon2Container } from '../../api/models/AndromeTV.types';

const landscapeSize = {
  SMALL: {
    width: 280,
    height: 157,
  },
  MEDIUM: {
    width: 340,
    height: 191,
  },
  NORMAL: {
    width: 400,
    height: 225,
  },
  LARGE: {
    width: 460,
    height: 259,
  },
  EXTRA_LARGE: {
    width: 520,
    height: 292,
  },
  MINI_EPG: {
    width: 410,
    height: 230,
  },
};
const landscapeSizeMap = new Map(Object.entries(landscapeSize));
const portraitSize = {
  SMALL: {
    width: 196,
    height: 280,
  },
  MEDIUM: {
    width: 240,
    height: 343,
  },
  NORMAL: {
    width: 280,
    height: 400,
  },
  LARGE: {
    width: 320,
    height: 457,
  },
  EXTRA_LARGE: {
    width: 364,
    height: 520,
  },
};
const portraitSizeMap = new Map(Object.entries(portraitSize));

export class CardSizeInfo {
  private _width: number | undefined;
  private _cardHeight: number | undefined;
  private _recipe: string | undefined;
  public gridMode: boolean;
  private _progressHeight: number;
  private _topLineHeight: number;
  private _bottomLineHeight: number;
  private _lineSpacing: number;
  showProgress: boolean;
  public showTopLine: boolean;
  public showBottomLine: boolean;
  cardType: CardType | undefined;
  private cardSize: CardSize | undefined;

  constructor(gridMode = false) {
    this._width = undefined;
    this._cardHeight = undefined;
    this._recipe = undefined;
    this.gridMode = gridMode;
    this._progressHeight = 0;
    this._topLineHeight = 0;
    this._bottomLineHeight = 0;
    this._lineSpacing = 0;
    this.showProgress = false;
    this.showTopLine = false;
    this.showBottomLine = false;
    this.cardType = CardType.Landscape;
  }

  get width(): number {
    if (this._width == null) {
      this.updateDimensions();
    }
    return this._width!;
  }

  get cardHeight(): number {
    if (this._cardHeight == null) {
      this.updateDimensions();
    }
    return this._cardHeight!;
  }

  get infoSectionHeight() {
    return (
      Number(this.showProgress) * this._progressHeight +
      Number(this.showTopLine || this.showBottomLine) *
        (this._topLineHeight + this._bottomLineHeight + this._lineSpacing + CmsConstants.Card.BottomPadding)
    );
  }

  get height(): number {
    if (this._cardHeight == null) {
      this.updateDimensions();
    }
    let height = this._cardHeight! + CmsConstants.Card.imageOffset + CmsConstants.Card.imageOffset;
    height += this.infoSectionHeight;
    return height;
  }

  get recipe(): string | undefined {
    if (this._recipe == null && this.cardType && this.cardSize) {
      this._recipe = imageRecipeForCard(this.cardType, this.cardSize);
    }
    return this._recipe;
  }

  get progressOffset() {
    return this._cardHeight;
  }

  get topLineOffset() {
    return this._cardHeight! + this._progressHeight;
  }

  get bottomLineOffset() {
    return this._cardHeight! + this._progressHeight + this._topLineHeight + this._lineSpacing;
  }

  updateDimensions() {
    if (!this.cardSize) this.cardSize = CardSize.Normal;
    if (this.cardType === CardType.Landscape) {
      this._width = landscapeSizeMap.get(this.cardSize)!.width + CmsConstants.Card.imageOffset * 2;
      this._cardHeight = landscapeSizeMap.get(this.cardSize)!.height;
    } else {
      this._width = this.gridMode ? 322 : portraitSizeMap.get(this.cardSize)!.width + CmsConstants.Card.imageOffset * 2;
      this._cardHeight = this.gridMode ? 450 : portraitSizeMap.get(this.cardSize)!.height;
    }
  }

  /**
   * Update card size for container data and page properties
   * @param {IRibbon2Container} data
   * @param {ICustomCmsRibbon} ribbonData
   * @param {string[]} cmsProperties
   */
  updateWithContainer(
    data: IRibbon2Container,
    cmsProperties: string[] = [],
    ribbonData: ICustomCmsRibbon | undefined = undefined
  ) {
    // console.log('[CardSizeInfo]::updateWithContainer', data, ribbonData, cmsProperties);
    this.cardType = data.type as CardType;
    if (data.items && data.items.length > 0) {
      this.cardSize = ribbonData
        ? ribbonData.cardSize
          ? (ribbonData.cardSize as CardSize)
          : CardSize.Normal
        : CardSize.Normal;
      if (cmsProperties.includes(CmsProperty.ShowProgress)) {
        this._progressHeight = CmsConstants.Progress.height;
        this.showProgress = true;
      } else {
        this._progressHeight = CmsConstants.Progress.hiddenHeight;
      }
      if (cmsProperties.includes(CmsProperty.HideInfo)) {
        if (cmsProperties.includes(CmsProperty.ShowEpisodeInfo)) {
          this._topLineHeight = CmsConstants.Card.TopLine;
          this.showTopLine = true;
        }
      } else {
        this._topLineHeight = CmsConstants.Card.TopLine;
        this.showTopLine = true;
      }
      if (this._topLineHeight > 0) {
        if (
          cmsProperties.includes(CmsProperty.ShowScheduleInfo) ||
          cmsProperties.includes(CmsProperty.UseBestSchedule)
        ) {
          this.showBottomLine = true;
          this.showProgress = true;
          this._progressHeight = CmsConstants.Progress.height;
          this._bottomLineHeight = CmsConstants.Card.BottomLine;
          this._lineSpacing = CmsConstants.Card.LineSpacing;
        }
      }
    } else {
      // message mode
    }
  }

  checkProgressBookmark(bookmarkPos: number | null) {
    if (!this.showTopLine && !this.showBottomLine && bookmarkPos == null) {
      this.showProgress = false;
    }
  }

  removeTopLine() {
    this._topLineHeight = 0;
    this.showTopLine = false;
  }

  setToListPageSettings() {
    this._topLineHeight = CmsConstants.Card.TopLine;
    this.showTopLine = true;
    this.showBottomLine = true;
    this.showProgress = true;
    this._progressHeight = CmsConstants.Progress.height;
    this._bottomLineHeight = CmsConstants.Card.BottomLine;
    this._lineSpacing = CmsConstants.Card.LineSpacing;
  }
}
